import { createContext, useContext, useState, useMemo, useCallback } from 'react';

const RealEstateContext = createContext(null);

// Property status options
export const PROPERTY_STATUS = {
  ACTIVE: 'Active',
  CLOSING: 'Closing',
  ON_CONTRACT: 'On Contract',
};

// Mock property data
const MOCK_PROPERTIES = [
  {
    id: 'p1',
    address: '123 Main Street, Jersey City, NJ 07302',
    price: 450000,
    status: PROPERTY_STATUS.ON_CONTRACT,
    buyerId: 'b1',
    buyerName: 'Michael Johnson',
    listingAgentId: 'a1',
    listingAgentName: 'Sarah Williams',
    statusDetails: 'Buyer waiting for appraisal. Expected completion by Feb 5.',
    bedrooms: 3,
    bathrooms: 2,
    sqft: 1800,
    listDate: '2025-12-01',
    lastUpdated: '2026-01-28',
  },
  {
    id: 'p2',
    address: '456 Oak Avenue, Hoboken, NJ 07030',
    price: 520000,
    status: PROPERTY_STATUS.ACTIVE,
    buyerId: null,
    buyerName: null,
    listingAgentId: 'a2',
    listingAgentName: 'David Brown',
    statusDetails: 'Open house scheduled for Saturday 2-4pm.',
    bedrooms: 2,
    bathrooms: 2,
    sqft: 1500,
    listDate: '2026-01-10',
    lastUpdated: '2026-01-25',
  },
  {
    id: 'p3',
    address: '789 Park Place, Newark, NJ 07102',
    price: 380000,
    status: PROPERTY_STATUS.CLOSING,
    buyerId: 'b2',
    buyerName: 'Jennifer Davis',
    listingAgentId: 'a1',
    listingAgentName: 'Sarah Williams',
    statusDetails: 'Final walkthrough completed. Closing scheduled for Feb 1.',
    bedrooms: 4,
    bathrooms: 2.5,
    sqft: 2200,
    listDate: '2025-11-15',
    lastUpdated: '2026-01-29',
  },
  {
    id: 'p4',
    address: '321 River Road, Edgewater, NJ 07020',
    price: 675000,
    status: PROPERTY_STATUS.ACTIVE,
    buyerId: null,
    buyerName: null,
    listingAgentId: 'a3',
    listingAgentName: 'Robert Miller',
    statusDetails: 'Price reduced. Expecting multiple offers.',
    bedrooms: 3,
    bathrooms: 2,
    sqft: 1950,
    listDate: '2026-01-05',
    lastUpdated: '2026-01-27',
  },
  {
    id: 'p5',
    address: '555 Boulevard East, Weehawken, NJ 07086',
    price: 890000,
    status: PROPERTY_STATUS.ON_CONTRACT,
    buyerId: 'b3',
    buyerName: 'Thomas Wilson',
    listingAgentId: 'a2',
    listingAgentName: 'David Brown',
    statusDetails: 'Inspection completed. Negotiating repairs.',
    bedrooms: 4,
    bathrooms: 3,
    sqft: 2800,
    listDate: '2025-12-20',
    lastUpdated: '2026-01-30',
  },
];

// Mock buyers data
const MOCK_BUYERS = [
  {
    id: 'b1',
    name: 'Michael Johnson',
    email: 'michael.j@email.com',
    phone: '(201) 555-0101',
    priceRange: { min: 400000, max: 500000 },
    bedrooms: 3,
    bathrooms: 2,
    preferredLocations: ['Jersey City', 'Hoboken'],
    preApproved: true,
    preApprovalAmount: 480000,
    notes: 'First-time buyer. Flexible on location.',
  },
  {
    id: 'b2',
    name: 'Jennifer Davis',
    email: 'jennifer.d@email.com',
    phone: '(973) 555-0202',
    priceRange: { min: 350000, max: 450000 },
    bedrooms: 4,
    bathrooms: 2,
    preferredLocations: ['Newark', 'East Orange'],
    preApproved: true,
    preApprovalAmount: 420000,
    notes: 'Relocating for work. Needs to close by March.',
  },
  {
    id: 'b3',
    name: 'Thomas Wilson',
    email: 'thomas.w@email.com',
    phone: '(201) 555-0303',
    priceRange: { min: 800000, max: 1000000 },
    bedrooms: 4,
    bathrooms: 3,
    preferredLocations: ['Weehawken', 'West New York'],
    preApproved: true,
    preApprovalAmount: 950000,
    notes: 'Looking for waterfront views. Cash buyer possible.',
  },
  {
    id: 'b4',
    name: 'Emily Martinez',
    email: 'emily.m@email.com',
    phone: '(201) 555-0404',
    priceRange: { min: 300000, max: 400000 },
    bedrooms: 2,
    bathrooms: 1,
    preferredLocations: ['Union City', 'North Bergen'],
    preApproved: false,
    preApprovalAmount: null,
    notes: 'Working on financing. Motivated buyer.',
  },
];

// Mock property viewings data
const MOCK_VIEWINGS = [
  {
    id: 'v1',
    buyerId: 'b1',
    propertyId: 'p1',
    propertyAddress: '123 Main Street, Jersey City, NJ 07302',
    dateSeen: '2026-01-15',
    price: 450000,
    compensation: 2.5,
    comments: 'Loved the kitchen. Concerned about street parking. Wants to see again.',
    offerSubmitted: true,
  },
  {
    id: 'v2',
    buyerId: 'b1',
    propertyId: 'p2',
    propertyAddress: '456 Oak Avenue, Hoboken, NJ 07030',
    dateSeen: '2026-01-18',
    price: 520000,
    compensation: 2.5,
    comments: 'Nice location but over budget. Will consider if price drops.',
    offerSubmitted: false,
  },
  {
    id: 'v3',
    buyerId: 'b2',
    propertyId: 'p3',
    propertyAddress: '789 Park Place, Newark, NJ 07102',
    dateSeen: '2026-01-10',
    price: 380000,
    compensation: 3.0,
    comments: 'Perfect fit for family. Made offer same day.',
    offerSubmitted: true,
  },
  {
    id: 'v4',
    buyerId: 'b3',
    propertyId: 'p5',
    propertyAddress: '555 Boulevard East, Weehawken, NJ 07086',
    dateSeen: '2026-01-12',
    price: 890000,
    compensation: 2.5,
    comments: 'Amazing views. Only concern is HOA fees.',
    offerSubmitted: true,
  },
  {
    id: 'v5',
    buyerId: 'b3',
    propertyId: 'p4',
    propertyAddress: '321 River Road, Edgewater, NJ 07020',
    dateSeen: '2026-01-20',
    price: 675000,
    compensation: 2.5,
    comments: 'Good property but prefers Weehawken location.',
    offerSubmitted: false,
  },
  {
    id: 'v6',
    buyerId: 'b4',
    propertyId: 'p2',
    propertyAddress: '456 Oak Avenue, Hoboken, NJ 07030',
    dateSeen: '2026-01-22',
    price: 520000,
    compensation: 2.5,
    comments: 'Likes it but needs to secure financing first.',
    offerSubmitted: false,
  },
];

// Mock agents data
const MOCK_AGENTS = [
  { id: 'a1', name: 'Sarah Williams', phone: '(201) 555-1001', email: 'sarah.w@century21.com', listings: 12 },
  { id: 'a2', name: 'David Brown', phone: '(201) 555-1002', email: 'david.b@century21.com', listings: 8 },
  { id: 'a3', name: 'Robert Miller', phone: '(201) 555-1003', email: 'robert.m@century21.com', listings: 15 },
];

export const RealEstateProvider = ({ children }) => {
  const [properties, setProperties] = useState(MOCK_PROPERTIES);
  const [buyers, setBuyers] = useState(MOCK_BUYERS);
  const [viewings, setViewings] = useState(MOCK_VIEWINGS);
  const [agents] = useState(MOCK_AGENTS);

  // Get all properties
  const getAllProperties = useCallback(() => {
    return properties;
  }, [properties]);

  // Get properties by status
  const getPropertiesByStatus = useCallback((status) => {
    return properties.filter((p) => p.status === status);
  }, [properties]);

  // Get property by ID
  const getPropertyById = useCallback((id) => {
    return properties.find((p) => p.id === id);
  }, [properties]);

  // Update property status
  const updatePropertyStatus = useCallback((propertyId, status, statusDetails) => {
    setProperties((prev) =>
      prev.map((p) =>
        p.id === propertyId
          ? { ...p, status, statusDetails, lastUpdated: new Date().toISOString().split('T')[0] }
          : p
      )
    );
  }, []);

  // Get all buyers
  const getAllBuyers = useCallback(() => {
    return buyers;
  }, [buyers]);

  // Get buyer by ID
  const getBuyerById = useCallback((id) => {
    return buyers.find((b) => b.id === id);
  }, [buyers]);

  // Get viewings for a buyer
  const getBuyerViewings = useCallback((buyerId) => {
    return viewings.filter((v) => v.buyerId === buyerId);
  }, [viewings]);

  // Add a new viewing
  const addViewing = useCallback((viewing) => {
    const newViewing = {
      ...viewing,
      id: `v${viewings.length + 1}`,
    };
    setViewings((prev) => [...prev, newViewing]);
    return newViewing;
  }, [viewings]);

  // Add a new property
  const addProperty = useCallback((property) => {
    const newProperty = {
      ...property,
      id: `p${properties.length + 1}`,
    };
    setProperties((prev) => [...prev, newProperty]);
    return newProperty;
  }, [properties]);

  // Add a new buyer
  const addBuyer = useCallback((buyer) => {
    const newBuyer = {
      ...buyer,
      id: `b${buyers.length + 1}`,
    };
    setBuyers((prev) => [...prev, newBuyer]);
    return newBuyer;
  }, [buyers]);

  // Get all agents
  const getAllAgents = useCallback(() => {
    return agents;
  }, [agents]);

  // Get agent by ID
  const getAgentById = useCallback((id) => {
    return agents.find((a) => a.id === id);
  }, [agents]);

  // Get statistics
  const getStatistics = useCallback(() => {
    const activeCount = properties.filter((p) => p.status === PROPERTY_STATUS.ACTIVE).length;
    const closingCount = properties.filter((p) => p.status === PROPERTY_STATUS.CLOSING).length;
    const onContractCount = properties.filter((p) => p.status === PROPERTY_STATUS.ON_CONTRACT).length;
    const totalValue = properties.reduce((sum, p) => sum + p.price, 0);
    const avgPrice = properties.length > 0 ? totalValue / properties.length : 0;

    return {
      totalProperties: properties.length,
      activeListings: activeCount,
      closingProperties: closingCount,
      onContractProperties: onContractCount,
      totalBuyers: buyers.length,
      preApprovedBuyers: buyers.filter((b) => b.preApproved).length,
      totalViewings: viewings.length,
      totalAgents: agents.length,
      totalValue,
      avgPrice,
    };
  }, [properties, buyers, viewings, agents]);

  const value = useMemo(
    () => ({
      properties,
      buyers,
      viewings,
      agents,
      getAllProperties,
      getPropertiesByStatus,
      getPropertyById,
      updatePropertyStatus,
      addProperty,
      getAllBuyers,
      getBuyerById,
      getBuyerViewings,
      addViewing,
      addBuyer,
      getAllAgents,
      getAgentById,
      getStatistics,
      PROPERTY_STATUS,
    }),
    [
      properties,
      buyers,
      viewings,
      agents,
      getAllProperties,
      getPropertiesByStatus,
      getPropertyById,
      updatePropertyStatus,
      addProperty,
      getAllBuyers,
      getBuyerById,
      getBuyerViewings,
      addViewing,
      addBuyer,
      getAllAgents,
      getAgentById,
      getStatistics,
    ]
  );

  return <RealEstateContext.Provider value={value}>{children}</RealEstateContext.Provider>;
};

export const useRealEstate = () => {
  const context = useContext(RealEstateContext);
  if (!context) {
    throw new Error('useRealEstate must be used within a RealEstateProvider');
  }
  return context;
};

export default RealEstateContext;
